/**
 * Capital Ask (slide 11) — client-side parser + assembler.
 * Iter59 (Feb 2026). Mirrors `lib/businessModel.js` / `lib/marketSize.js`.
 *
 * Iter72 (Feb 2026) — Use-of-Funds bullets carry a `pct`.
 * Iter73 (Feb 2026) — the block now records `instrument` (equity /
 * debt / both) with an optional freeform note (e.g. "60% equity, 40%
 * venture debt") so funders see how the raise is being structured.
 *
 * Canonical format:
 *
 *   Capital Ask
 *   5M
 *
 *   Instrument
 *   Both — 60% equity, 40% venture debt
 *
 *   Use of Funds
 *   - Product & engineering hires (35%)
 *   ...
 */

export const BULLET_WORD_CAP = 12;
export const MAX_USE_BULLETS = 5;
export const MAX_MILESTONES = 3;

/** Allowed instrument kinds. `""` = unset. */
export const INSTRUMENT_KINDS = ["", "equity", "debt", "both"];

const PCT_SUFFIX_RE = /\s*\(([0-9]+(?:\.[0-9]+)?)\s*%\)\s*$/;

export function splitBulletPct(raw) {
  const s = (raw || "").trim();
  if (!s) return { text: "", pct: null };
  const m = s.match(PCT_SUFFIX_RE);
  if (!m) return { text: s, pct: null };
  const pct = parseFloat(m[1]);
  if (!Number.isFinite(pct)) return { text: s, pct: null };
  return { text: s.replace(PCT_SUFFIX_RE, "").trim(), pct };
}

/** Parse an "Instrument" block body into `{kind, note}`.
 *  Acceptable shapes:
 *    "Equity"                              → {kind: "equity",  note: ""}
 *    "Debt"                                → {kind: "debt",    note: ""}
 *    "Both"                                → {kind: "both",    note: ""}
 *    "Both — 60% equity, 40% venture debt" → {kind: "both", note: "60% equity, 40% venture debt"}
 *    "Equity (SAFE, uncapped)"             → {kind: "equity",  note: "SAFE, uncapped"}
 */
function parseInstrumentBody(lines) {
  const raw = lines.join(" ").trim();
  if (!raw) return { kind: "", note: "" };
  // Peel "kind — note" or "kind - note" or "kind (note)"
  const dashSplit = raw.split(/\s+[—–-]\s+/);
  let firstToken = dashSplit[0];
  let note = dashSplit.length > 1 ? dashSplit.slice(1).join(" - ").trim() : "";
  // Also accept "kind (note)" — strip trailing parens off firstToken.
  const parenMatch = firstToken.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (parenMatch && !note) {
    firstToken = parenMatch[1].trim();
    note = parenMatch[2].trim();
  }
  const k = firstToken.trim().toLowerCase();
  if (k === "equity" || k === "debt" || k === "both") {
    return { kind: k, note };
  }
  // Unknown first token → treat whole line as a note, no kind selected.
  return { kind: "", note: raw };
}

export function parseCapitalAsk(content) {
  const out = { amount: "", instrument: { kind: "", note: "" }, bullets: [], milestones: [] };
  if (!content) return out;
  const blocks = content.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  for (const block of blocks) {
    const lines = block.split(/\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;
    const header = lines[0].toLowerCase();
    if (header === "capital ask" || header === "the ask") {
      out.amount = lines.slice(1).join(" ").trim();
    } else if (header === "instrument") {
      out.instrument = parseInstrumentBody(lines.slice(1));
    } else if (header === "use of funds") {
      out.bullets = lines.slice(1)
        .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
        .filter(Boolean)
        .slice(0, MAX_USE_BULLETS)
        .map(splitBulletPct);
    } else if (header === "milestones") {
      out.milestones = lines.slice(1)
        .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
        .filter(Boolean)
        .slice(0, MAX_MILESTONES);
    }
  }
  return out;
}

/** Label used in canonical strings AND rendered in the preview. */
export function instrumentLabel(inst) {
  if (!inst || !inst.kind) return "";
  const kindLabel = inst.kind === "equity" ? "Equity"
    : inst.kind === "debt" ? "Debt"
      : inst.kind === "both" ? "Both" : "";
  if (!kindLabel) return "";
  const note = (inst.note || "").trim();
  return note ? `${kindLabel} — ${note}` : kindLabel;
}

export function assembleCapitalAsk({ amount, instrument, bullets, milestones }) {
  const blocks = [];
  const amt = (amount || "").trim();
  if (amt) blocks.push(`Capital Ask\n${amt}`);
  const instLine = instrumentLabel(instrument);
  if (instLine) blocks.push(`Instrument\n${instLine}`);
  const bs = (bullets || [])
    .map((b) => {
      if (typeof b === "string") return { text: b.trim(), pct: null };
      return { text: (b.text || "").trim(), pct: b.pct };
    })
    .filter((b) => b.text)
    .slice(0, MAX_USE_BULLETS);
  if (bs.length > 0) {
    const lines = bs.map(({ text, pct }) => {
      const pctSuffix = (pct != null && Number.isFinite(pct) && pct > 0)
        ? ` (${Number.isInteger(pct) ? pct : Number(pct).toFixed(1)}%)`
        : "";
      return `- ${text}${pctSuffix}`;
    });
    blocks.push(`Use of Funds\n${lines.join("\n")}`);
  }
  const ms = (milestones || []).map((m) => (m || "").trim()).filter(Boolean).slice(0, MAX_MILESTONES);
  if (ms.length > 0) blocks.push(`Milestones\n${ms.map((m) => `- ${m}`).join("\n")}`);
  return blocks.join("\n\n");
}

export function countWords(s) {
  if (!s) return 0;
  const t = s.trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
}
